import {
  ANCHOR_OFFSET_FROM_STICKY_HEADER,
  BrandPageBackButton,
  BrandPageFollowButton,
  BrandPageHeader,
  BrandPageStream,
  BrandShopInlineFilter,
  BrandShopMeta,
  BrandShopNavigationTree,
  BrandShopStreamHeader,
  InteractiveProductTile,
  LoadMoreComponentDisplayConfig,
  ProductMiniAdpWithBasketFlyout,
  SizeSelectionOpenedStateKey,
  STREAM_TOP_SPACING,
  useMiniAdpDialogOpenedState,
  useProductStreamDisplayState,
} from '@aboutyou/logic-components';
import {ScrollTopAnchor} from '@aboutyou/router';
import {
  COLORS,
  HeaderCircleButton,
  SplitView,
  Z_INDEXES,
} from '@aboutyou/ui-components';
import React, {StrictMode, Suspense, useRef} from 'react';
import {Sticky} from 'react-stickup-ay';
import styled from 'styled-components';
import {useADPPreload} from '../ArticleDetailPage/useADPPreload';

export const BrandShopPage: React.FC = () => {
  const {productImageType, updateProductImageType} =
    useProductStreamDisplayState();
  const sidebarRef = useRef<HTMLDivElement>(null);

  const loadMoreComponentDisplayConfig: LoadMoreComponentDisplayConfig = {
    tilesBeforeFirstDisplay: 10000,
    tilesBeforeConsecutivelyDisplay: 10000,
  };

  const {open: openProductMiniADPDialog} = useMiniAdpDialogOpenedState();

  const inlineFiltersRef = useRef<HTMLDivElement | null>(null);

  useADPPreload();

  return (
    <StrictMode>
      <BrandShopMeta />

      <BrandPageHeader
        HeaderElements={({brand, linksSection}) => (
          <>
            <BrandPageBackButton
              linksSection={linksSection}
              BackButtonComponent={HeaderCircleButton}
              iconSize="XS"
              iconColor={COLORS.Black}
            />
            <BrandPageFollowButton brand={brand} />
          </>
        )}
        landscape
      />

      <ScrollTopAnchor
        defaultOffsetTop={ANCHOR_OFFSET_FROM_STICKY_HEADER}
        as={SplitView}
        split={'30:70'}
        splitMedium={'20:80'}
        splitLarge={'15:85'}
        reducePadding
        refStart={sidebarRef}
        contentStart={
          <Suspense>
            <Sticky
              overflowScroll="flow"
              experimentalNative
              container={sidebarRef}
              style={{zIndex: Z_INDEXES.CATEGORY_TREE}}
              defaultOffsetTop={ANCHOR_OFFSET_FROM_STICKY_HEADER}
            >
              <StyledBrandShopNavigationTree />
            </Sticky>
          </Suspense>
        }
        contentEnd={
          <ContentSection>
            <BrandShopStreamHeader
              productImageType={productImageType}
              onChangeProductImageType={updateProductImageType}
              inlineFiltersRef={inlineFiltersRef}
            />
            <FiltersAndProductStreamWrapper>
              <BrandShopInlineFilter inlineFiltersRef={inlineFiltersRef} />
              <Suspense>
                <BrandPageStream
                  constrainStoryTilesToViewportBoundary
                  gridColumns={undefined}
                  loadMoreComponentDisplayConfig={
                    loadMoreComponentDisplayConfig
                  }
                  interactiveHoverMode
                  onClickAddCta={openProductMiniADPDialog}
                  ProductTileComponent={InteractiveProductTile}
                  layoutConfig={{layout: 'desktop'}}
                />
              </Suspense>
            </FiltersAndProductStreamWrapper>
          </ContentSection>
        }
      />

      <ProductMiniAdpWithBasketFlyout
        showAddToWishlistButton
        sizeSelectionStateManagerKey={
          SizeSelectionOpenedStateKey.MAIN_SIZE_SELECTION
        }
      />
    </StrictMode>
  );
};

const ContentSection = styled.section`
  padding-top: ${STREAM_TOP_SPACING}px;
`;

const StyledBrandShopNavigationTree = styled(BrandShopNavigationTree)`
  padding-top: ${STREAM_TOP_SPACING}px;
  word-break: break-word;
`;

const FiltersAndProductStreamWrapper = styled.div`
  overflow: hidden;
`;
